import { createHashHistory } from 'history';

/**
 * Sanitizes a URL string to prevent URIError from invalid percent-encoding.
 * This handles cases like trailing % signs or incomplete percent sequences.
 */
function sanitizeUrl(urlString) {
  if (!urlString) return urlString;
  try {
    // Try to decode - if it works, the URL is valid
    decodeURIComponent(urlString);
    return urlString;
  } catch (e) {
    // Invalid percent-encoding detected - replace invalid % sequences with %25 (encoded %)
    return urlString.replace(/%(?![0-9A-Fa-f]{2})/g, '%25');
  }
}

/**
 * Sanitizes the current window.location.hash and updates it if necessary
 */
function sanitizeCurrentHash() {
  if (window.location.hash) {
    const hashWithoutSymbol = window.location.hash.slice(1);
    let sanitizedHash = sanitizeUrl(hashWithoutSymbol);
    // react-router v6 routes are rooted at `/`. Emailed links omit the leading
    // slash (e.g. `#activate/TOKEN`), so restore it or no route matches (blank).
    if (sanitizedHash && !sanitizedHash.startsWith('/')) {
      sanitizedHash = `/${sanitizedHash}`;
    }
    if (sanitizedHash !== hashWithoutSymbol) {
      window.location.hash = sanitizedHash;
    }
  }
}

export function navigateTo(newLocation) {
  if (!newLocation) {
    throw new Error('navigateTo was called without a newLocation');
  }
  window.location = `#${sanitizeUrl(newLocation)}`;
}
export function navigateToURL(newLocation) {
  if (!newLocation) {
    throw new Error('navigateToURL was called without a newLocation');
  }
  window.location = sanitizeUrl(newLocation);
}

export function openInNewTab(url) {
  window.open(sanitizeUrl(url), '_blank').focus();
}

/**
 * Add event listener to sanitize hash on any hash change
 * This is needed since we use history.listen throughout the app
 */

window.addEventListener('hashchange', sanitizeCurrentHash);

export const history = (function () {
  sanitizeCurrentHash();
  return createHashHistory();
})();

export const reactRouterNavigateTo = (path, state) => history.push(sanitizeUrl(path), state);
export const reactRouterBack = () => history.back();
export const reactRouterReplace = (path, state) => history.replace(sanitizeUrl(path), state);
